/**
 * Admin Sign-in — dedicated, discoverable entry point for the admin
 * dashboard. Reuses the same /api/auth/login endpoint as the regular
 * user Login page (same JWT cookies, same security model). Not a
 * separate auth system — just a styled clone with role-aware redirect.
 *
 * Behavior:
 *   • role === 'admin'  → /admin
 *   • role !== 'admin'  → inline "restricted" message + Sign-out CTA
 *   • already-signed-in admin landing here → auto-redirect to /admin
 *   • already-signed-in non-admin landing here → same restricted state
 */
/* eslint-disable */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, ShieldCheck, Lock, ArrowLeft, ShieldAlert, Eye, EyeOff } from 'lucide-react';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { user, login, logout } = useAuth();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // "restricted" — set when a non-admin attempts to use this page,
  // either by submitting credentials or by visiting while already
  // signed in as a regular user. Surfaces a clear inline message
  // rather than silently bouncing them.
  const [restricted, setRestricted] = useState(false);

  // If an admin is already signed in, this page is just a transit —
  // send them straight to the dashboard. Non-admin already-signed-in
  // users see the restricted banner below.
  useEffect(() => {
    if (user && user.role === 'admin') {
      navigate('/admin', { replace: true });
    } else if (user && user.role && user.role !== 'admin') {
      setRestricted(true);
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setRestricted(false);
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (!result.success) {
      setError(result.error);
      return;
    }

    if (result.user?.role === 'admin') {
      navigate('/admin');
    } else {
      // Signed them in as a regular user successfully, but they don't
      // have the admin claim. Show the restricted state and let them
      // sign out so the next admin can use this device.
      setRestricted(true);
    }
  };

  const handleSignOut = async () => {
    await logout();
    setRestricted(false);
    setEmail('');
    setPassword('');
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 p-6"
      data-testid="admin-login-page"
    >
      <Card className="w-full max-w-md shadow-2xl border-0 rounded-2xl bg-white" data-testid="admin-login-card">
        <CardContent className="pt-9 pb-9 px-8">
          {/* Back link — back to the public user login for normal users
              who landed here by mistake. */}
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-6 transition-colors"
            data-testid="admin-login-back-user-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t('auth.admin.backToUserLogin')}
          </Link>

          {/* Header — distinct from user /login: dark accent, shield
              icon, "Admin" badge. No marketing copy. */}
          <div className="flex flex-col items-center mb-7">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-900 to-indigo-900 flex items-center justify-center shadow-lg shadow-indigo-900/20 mb-4 ring-1 ring-indigo-500/20">
              <ShieldCheck className="h-7 w-7 text-indigo-200" />
            </div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-xl font-semibold text-slate-900 tracking-tight">{t('auth.admin.title')}</h1>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-indigo-100 text-indigo-700 border border-indigo-200">
                {t('auth.admin.restrictedBadge')}
              </span>
            </div>
            <p className="text-xs text-slate-500 text-center">
              {t('auth.admin.tagline')}
            </p>
          </div>

          {/* Restricted state — shown when a non-admin signs in here or
              lands while already authenticated as a regular user. */}
          {restricted ? (
            <div data-testid="admin-login-restricted">
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-4 flex items-start gap-3">
                <ShieldAlert className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-900 leading-relaxed">
                  <p className="font-medium mb-1">{t('auth.admin.restrictedTitle')}</p>
                  <p className="text-xs text-amber-800/90">
                    {t('auth.admin.restrictedBody')}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={handleSignOut}
                  className="flex-1 h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-xl"
                  data-testid="admin-login-restricted-signout"
                >
                  {t('auth.admin.signOut')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="flex-1 h-11 rounded-xl"
                  data-testid="admin-login-restricted-back"
                >
                  {t('auth.admin.backToApp')}
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div
                  className="bg-rose-50 text-rose-700 p-3 rounded-xl text-sm border border-rose-100"
                  data-testid="admin-login-error"
                  role="alert"
                >
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="admin-email" className="text-sm font-medium text-slate-700">
                  {t('auth.admin.emailLabel')}
                </Label>
                <Input
                  id="admin-email"
                  type="email"
                  autoComplete="email"
                  placeholder={t('auth.admin.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 border-slate-200 rounded-xl focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/10 transition-all placeholder:text-slate-400"
                  data-testid="admin-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-password" className="text-sm font-medium text-slate-700">
                  {t('auth.admin.passwordLabel')}
                </Label>
                <div className="relative">
                  <Input
                    id="admin-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder={t('auth.admin.passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 border-slate-200 rounded-xl focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/10 transition-all placeholder:text-slate-400 pr-12"
                    data-testid="admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center h-9 w-9 text-slate-400 hover:text-slate-600 rounded-md transition-colors"
                    aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                    aria-pressed={showPassword}
                    tabIndex={-1}
                    data-testid="admin-login-password-toggle"
                  >
                    {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="pt-1">
                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-slate-900 to-indigo-900 hover:from-slate-800 hover:to-indigo-800 text-white font-semibold rounded-xl shadow-lg shadow-indigo-900/20 hover:shadow-xl transition-all duration-200 active:scale-[0.97]"
                  disabled={loading}
                  data-testid="admin-login-submit"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('auth.admin.signingIn')}
                    </span>
                  ) : (
                    t('auth.admin.submit')
                  )}
                </Button>
              </div>

              <div className="mt-2 text-center">
                <div className="flex items-center justify-center gap-1.5 text-slate-400">
                  <Lock className="h-3 w-3" />
                  <p className="text-[11px]">{t('auth.admin.secureNote')}</p>
                </div>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
